import { Box, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { getAuthUserSelector } from '@sophocles/features/auth/authSelectors';
import { calculatePresetRange, getDefaultDateRangePresets } from '@sophocles/utils/dateRangePresets';
import { formatDate } from '@sophocles/utils/formatUtils';

/**
 * RelativeDateRangePicker - A reusable component for selecting date ranges
 * Supports both relative ranges (e.g., "Last 30 days") and custom date selection
 *
 * @param {Object} props
 * @param {Date|null} props.startDate - The current start date
 * @param {Date|null} props.endDate - The current end date
 * @param {Function} props.onChange - Callback when date range changes: (startDate, endDate, rangeType) => void
 * @param {string} props.label - Label for the component
 * @param {Array} props.presets - Array of preset options (optional, uses defaults if not provided)
 * @param {boolean} props.includeTime - Whether to include time in the date selection (default: false)
 */
const RelativeDateRangePicker = ({
  startDate = null,
  endDate = null,
  onChange,
  label = 'Date Range',
  presets = null,
  includeTime = false,
}) => {
  const { t } = useTranslation();
  const user = useSelector(getAuthUserSelector);
  const [selectedValue, setSelectedValue] = useState('last30days');

  const presetOptions = presets || getDefaultDateRangePresets(t);

  // Handle selection change
  const handleSelectionChange = event => {
    const value = event.target.value;
    setSelectedValue(value);

    if (value !== 'custom') {
      const preset = presetOptions.find(p => p.value === value);
      if (preset) {
        const { start, end } = calculatePresetRange(preset, { includeTime });
        onChange(start, end, 'relative', value);
      }
    }
    // If custom, user will select dates manually
  };

  // Handle custom date changes
  const handleStartDateChange = newStartDate => {
    onChange(newStartDate, endDate, 'custom');
  };

  const handleEndDateChange = newEndDate => {
    onChange(startDate, newEndDate, 'custom');
  };

  // Initialize with default preset if no dates provided
  useEffect(() => {
    if (!startDate && !endDate && selectedValue !== 'custom') {
      const preset = presetOptions.find(p => p.value === selectedValue);
      if (preset) {
        const { start, end } = calculatePresetRange(preset, { includeTime });
        onChange(start, end, 'relative', selectedValue);
      }
    }
  }, []); // Only run on mount

  const isCustom = selectedValue === 'custom';

  // Generate helper text for preset selections
  const helperText =
    !isCustom && startDate && endDate ? `${formatDate(startDate, user)} - ${formatDate(endDate, user)}` : '';

  return (
    <Box>
      <FormControl fullWidth size="small">
        {label && <InputLabel>{label}</InputLabel>}
        <Select value={selectedValue} onChange={handleSelectionChange} label={label}>
          {presetOptions.map(preset => (
            <MenuItem key={preset.value} value={preset.value}>
              {preset.label}
            </MenuItem>
          ))}
          <MenuItem value="custom">{t('dateRange.custom', { defaultValue: 'Custom' })}</MenuItem>
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>

      {isCustom && (
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label={t('dateRange.startDate', { defaultValue: 'Start Date' })}
              value={startDate}
              onChange={handleStartDateChange}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              maxDate={endDate || undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <DatePicker
              label={t('dateRange.endDate', { defaultValue: 'End Date' })}
              value={endDate}
              onChange={handleEndDateChange}
              slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              minDate={startDate || undefined}
            />
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default RelativeDateRangePicker;
